/**
 * @file     JourneyMap.tsx
 * @readme   ./README.md
 * @purpose  Vertical journey map sidebar, Duolingo-style node connector showing modules and lessons.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme';
import type { JourneyConfig, LessonStatus } from './types';
import { JourneyNode } from './JourneyNode';
import { useAcademy } from './AcademyContext';
import { isLessonFree } from './gating';
import { useAuth } from '@/hooks/useAuth';

interface JourneyMapProps {
  journey: JourneyConfig;
  activeModuleSlug?: string;
  activeLessonSlug?: string;
}

export function JourneyMap({ journey, activeModuleSlug, activeLessonSlug }: JourneyMapProps) {
  const { theme } = useAppTheme();
  const { progress } = useAcademy();
  const { isAuthenticated } = useAuth();

  function getLessonStatus(lessonId: string, moduleIndex: number, lessonIndex: number): LessonStatus {
    if (progress[lessonId]?.status === 'completed') return 'completed';
    if (progress[lessonId]?.status === 'started') return 'started';
    const free = isLessonFree(journey, moduleIndex, lessonIndex);
    if (free || isAuthenticated) return 'available';
    return 'locked';
  }

  return (
    <Box
      component="nav"
      aria-label="Learning journey"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        py: 2,
      }}
    >
      {journey.modules.map((mod, moduleIndex) => (
        <Box key={mod.id} sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{
              fontFamily: theme.typography.fontFamilyHeading,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1.2,
              color: theme.semanticColors.textMuted,
              textTransform: 'uppercase',
              px: 2,
              mb: 0.5,
              display: 'block',
            }}
          >
            {mod.titleDefault}
          </Typography>

          {mod.lessons.map((lesson, lessonIndex) => {
            const status = getLessonStatus(lesson.id, moduleIndex, lessonIndex);
            const isActive = mod.slug === activeModuleSlug && lesson.slug === activeLessonSlug;
            const isLast = lessonIndex === mod.lessons.length - 1;

            return (
              <JourneyNode
                key={lesson.id}
                lesson={lesson}
                status={status}
                isActive={isActive}
                showConnector={!isLast}
                audienceSlug={journey.slug}
                moduleSlug={mod.slug}
              />
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
